import type { UiCommandBusV2, UiManagerMainRoute, UiSnapshotV2 } from "@/ui/runtime/contracts/manager-ui-v2";
import { Badge } from "@/ui/foundation/primitives/badge";
import { Button } from "@/ui/foundation/primitives/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/foundation/primitives/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui/foundation/primitives/table";
import { cn } from "@/ui/foundation/lib/utils";
import { FormResultPill, formatFriendlyDate, normalizeForm, renderPips } from "@/ui/components/manager/manager-ui-helpers";

interface HomeScreenProps {
    snapshot: UiSnapshotV2;
    commands: UiCommandBusV2;
    onNavigate(route: UiManagerMainRoute): void;
}

export function HomeScreen({ snapshot, commands, onNavigate }: HomeScreenProps) {
    const completedFixtures = snapshot.routes.fixtures.fixtures
        .filter((fixture) => fixture.status === "completed" && (fixture.home === snapshot.teamName || fixture.away === snapshot.teamName))
        .sort((a, b) => (b.date + String(b.matchday)).localeCompare(a.date + String(a.matchday)))
        .slice(0, 3);
    const squadPlayers = snapshot.routes.squad.players;
    const averageReadiness = squadPlayers.length
        ? Math.round(squadPlayers.reduce((sum, player) => sum + player.readiness, 0) / squadPlayers.length)
        : 0;
    const averageMorale = squadPlayers.length
        ? Math.round(
            squadPlayers.reduce((sum, player) => {
                const moraleScore = player.morale === "Good" ? 75 : player.morale === "Okay" ? 55 : 35;
                return sum + moraleScore;
            }, 0) / squadPlayers.length
        )
        : 0;
    const averageRating = squadPlayers.length
        ? Math.round(squadPlayers.reduce((sum, player) => sum + player.rating, 0) / squadPlayers.length)
        : 0;

    return (
        <>
            <div className="grid gap-4 md:grid-cols-3">
                <Card className="border-primary/35 bg-primary/5">
                    <CardHeader>
                        <CardDescription>Today</CardDescription>
                        <CardTitle>{snapshot.routes.home.summary.dateLabel.replace("Date: ", "")}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">{snapshot.routes.home.summary.nextLabel}</CardContent>
                </Card>
                <Card className="border-primary/25 bg-card">
                    <CardHeader>
                        <CardDescription>Finances</CardDescription>
                        <CardTitle>{snapshot.routes.home.summary.financeLabel.replace("Budget: ", "")}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                        Weekly wage bill: £{Math.round(snapshot.routes.home.summary.wagesPerWeek).toLocaleString()}
                    </CardContent>
                </Card>
                <Card className="border-amber-400/30 bg-amber-500/5">
                    <CardHeader>
                        <CardDescription>Attention</CardDescription>
                        <CardTitle className="flex items-center gap-2">Inbox <Badge variant="secondary">{snapshot.routes.home.inboxBadgeCount}</Badge></CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                                onNavigate("/manager/inbox");
                                void commands.setManagerScreen("inbox");
                            }}
                        >
                            Open Inbox
                        </Button>
                    </CardContent>
                </Card>
            </div>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-3">
                    <CardTitle>Competition Snapshot</CardTitle>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            onNavigate("/manager/competitions");
                        }}
                    >
                        View Full Table
                    </Button>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10 px-2">Pos</TableHead>
                                <TableHead>Club</TableHead>
                                <TableHead className="text-right">Played</TableHead>
                                <TableHead className="text-right">Won</TableHead>
                                <TableHead className="text-right">Drawn</TableHead>
                                <TableHead className="text-right">Lost</TableHead>
                                <TableHead className="text-right">GF</TableHead>
                                <TableHead className="text-right">GA</TableHead>
                                <TableHead className="text-right">GD</TableHead>
                                <TableHead className="text-right">Pts</TableHead>
                                <TableHead className="text-right">Form</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {snapshot.routes.home.leagueTable.map((row) => (
                                <TableRow key={`${row.position}-${row.club}`} className={cn(row.club === snapshot.teamName ? "bg-primary/10" : "")}>
                                    <TableCell className="px-2">{row.position}</TableCell>
                                    <TableCell className={cn(row.club === snapshot.teamName ? "font-semibold text-primary" : "")}>{row.club}</TableCell>
                                    <TableCell className="text-right">{row.played}</TableCell>
                                    <TableCell className="text-right">{row.won}</TableCell>
                                    <TableCell className="text-right">{row.drawn}</TableCell>
                                    <TableCell className="text-right">{row.lost}</TableCell>
                                    <TableCell className="text-right">{row.gf}</TableCell>
                                    <TableCell className="text-right">{row.ga}</TableCell>
                                    <TableCell className="text-right">{row.gd}</TableCell>
                                    <TableCell className="text-right text-base font-semibold">{row.points}</TableCell>
                                    <TableCell>
                                        <div className="flex justify-end gap-1">
                                            {normalizeForm(row.form).map((code, index) => (
                                                <FormResultPill key={`${row.club}-${index}`} code={code} />
                                            ))}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>Recent Results</CardTitle>
                        <CardDescription>Latest managed team fixtures</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Home</TableHead>
                                    <TableHead>Score</TableHead>
                                    <TableHead>Away</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {completedFixtures.length > 0 ? completedFixtures.map((fixture) => (
                                    <TableRow key={`${fixture.date}-${fixture.home}-${fixture.away}-${fixture.matchday}`}>
                                        <TableCell>{formatFriendlyDate(fixture.date)}</TableCell>
                                        <TableCell>{fixture.home}</TableCell>
                                        <TableCell>{fixture.homeScore ?? 0}-{fixture.awayScore ?? 0}</TableCell>
                                        <TableCell>{fixture.away}</TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-sm text-muted-foreground">No completed managed-team fixtures yet.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle>Squad Readiness</CardTitle>
                        <CardDescription>Training and fatigue indicators</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm">Match Fitness</span>
                            {renderPips(averageReadiness)}
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm">Morale</span>
                            {renderPips(averageMorale)}
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm">Average Player Quality</span>
                            {renderPips(averageRating)}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
